import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "./Reveal";
import Glyph from "./Glyphs";
import { projects, profile } from "../site";

/**
 * Full page for a single portfolio project: /portfolio/<slug>
 * Deep links work in production because hosting rewrites everything to index.html.
 */
export default function ProjectPage() {
  const { slug } = useParams();
  const index = projects.items.findIndex((item) => item.slug === slug);
  const item = index >= 0 ? projects.items[index] : null;
  const next = projects.items.length > 1 ? projects.items[(index + 1) % projects.items.length] : null;

  useEffect(() => {
    if (!item) return undefined;

    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDesc = meta ? meta.getAttribute("content") : null;

    document.title = `${item.title} | ${profile.name}`;
    if (meta && item.card) meta.setAttribute("content", item.card);

    return () => {
      document.title = previousTitle;
      if (meta && previousDesc) meta.setAttribute("content", previousDesc);
    };
  }, [item]);

  if (!item) {
    return (
      <main className="page">
        <section className="section proj">
          <div className="shell">
            <span className="label">Portfolio</span>
            <h1 className="proj__title">That project is not listed.</h1>
            <p className="proj__summary">
              It may have been renamed or removed. Everything current is on the portfolio page.
            </p>
            <div className="proj__links">
              <Link className="btn btn--primary" to="/#portfolio">
                Back to portfolio
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="page">
      <section className="section proj">
        <div className="shell">
          <Reveal>
            <Link className="proj__back" to="/#portfolio">
              <Glyph name="arrow-left" />
              Portfolio
            </Link>

            <div className="proj__meta">
              <span className="proj__cat">{item.category}</span>
              {item.period ? <span className="proj__period">{item.period}</span> : null}
              {item.client ? <span className="proj__period">{item.client}</span> : null}
            </div>

            <h1 className="proj__title">{item.title}</h1>
            {item.role ? <p className="proj__role">{item.role}</p> : null}
            <p className="proj__summary">{item.summary}</p>
          </Reveal>

          {item.image ? (
            <Reveal delay={80}>
              <figure className="proj__media">
                <img
                  src={item.image}
                  alt={item.imageAlt || item.title}
                  width={item.imageW || 1024}
                  height={item.imageH || 768}
                  decoding="async"
                />
              </figure>
            </Reveal>
          ) : null}

          {item.did || item.results ? (
            <Reveal delay={120} className="proj__cols">
              {item.did ? (
                <div className="proj__col">
                  <h2 className="proj__colTitle">What I did</h2>
                  <ul className="proj__list">
                    {item.did.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {item.results ? (
                <div className="proj__col">
                  <h2 className="proj__colTitle">Results</h2>
                  <ul className="proj__list proj__list--results">
                    {item.results.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </Reveal>
          ) : null}

          {item.tags ? (
            <Reveal delay={140}>
              <div className="proj__tags">
                {item.tags.map((tag) => (
                  <span className="chip" key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </Reveal>
          ) : null}

          {item.links ? (
            <Reveal delay={160}>
              <div className="proj__links">
                {item.links.map((link) => (
                  <a
                    className="btn btn--ghost"
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </Reveal>
          ) : null}

          {next ? (
            <Reveal delay={180}>
              <Link className="proj__next" to={`/portfolio/${next.slug}`}>
                <span className="proj__nextBody">
                  <span className="proj__nextLabel">Next project</span>
                  <span className="proj__nextTitle">{next.title}</span>
                </span>
                <span className="proj__nextIcon" aria-hidden="true">
                  <Glyph name="arrow-up" />
                </span>
              </Link>
            </Reveal>
          ) : null}
        </div>
      </section>
    </main>
  );
}
